// src/pages/RegisterPage.js - Updated with admin approval flow
"use client"

import { useState } from "react"
import { Link } from "react-router-dom"
import { Clock, CheckCircle, Mail, AlertCircle } from "lucide-react"

const RegisterPage = () => {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [message, setMessage] = useState("")
  const [loading, setLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isPending, setIsPending] = useState(false)

  const handleRegister = async (e) => {
    e.preventDefault()
    setLoading(true)
    setMessage("")
    setIsSuccess(false)
    setIsPending(false)

    try {
      const response = await fetch("https://call-analyzer-backend.onrender.com/signup-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, password }),
      })

      const data = await response.json()

      if (response.ok) {
        setIsSuccess(true)
        setIsPending(true)
        setMessage("Registration successful!")

        // Clear form
        setName("")
        setEmail("")
        setPassword("")
      } else {
        setMessage(data.detail || "Registration failed. Please try again.")
        setIsSuccess(false)
      }
    } catch (err) {
      setMessage("Server error. Please try again later.")
      setIsSuccess(false)
    } finally {
      setLoading(false)
    }
  }

  // Show pending approval screen after successful registration
  if (isPending) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-[#CED7DB] flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white/80 backdrop-blur-sm p-8 rounded-xl shadow-lg border border-[#CED7DB]/30 text-center">
            <div className="mb-6">
              <div className="mx-auto w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                <Clock className="h-8 w-8 text-yellow-600" />
              </div>
              <h1 className="text-2xl font-bold text-[#0A4B4B] mb-2">Registration Submitted!</h1>
              <p className="text-gray-600">Your account is pending admin approval</p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                <div className="text-left">
                  <h3 className="text-sm font-medium text-yellow-800">What happens next?</h3>
                  <ul className="text-sm text-yellow-700 mt-2 space-y-1">
                    <li>1. An admin will review your registration</li>
                    <li>2. You'll receive approval via email</li>
                    <li>3. Then you can log in and start using the platform</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center">
                <Mail className="h-5 w-5 text-blue-600 mr-2" />
                <span className="text-sm text-blue-700">
                  Keep an eye on your inbox for approval notification
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <Link
                to="/login"
                className="block w-full bg-[#0A4B4B] hover:bg-[#083d3d] text-white py-3 px-4 rounded-lg font-semibold transition-all duration-200 text-center"
              >
                Go to Login
              </Link>
              <Link
                to="/"
                className="block text-sm text-gray-600 hover:text-[#0A4B4B] transition-colors"
              >
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-[#CED7DB] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <img src="/New_Logo.png" alt="NeuroTrain Logo" className="mx-auto h-12 mb-4" />
          <h1 className="text-2xl font-bold text-[#0A4B4B] mb-2">Create Your Account</h1>
          <p className="text-gray-600">Request access to NeuroTrain</p>
        </div>

        {/* Registration Form */}
        <div className="bg-white/80 backdrop-blur-sm p-8 rounded-xl shadow-lg border border-[#CED7DB]/30">
          <form onSubmit={handleRegister} className="space-y-6">
            {message && !isSuccess && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {message}
              </div>
            )}

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <input
                id="name"
                type="text"
                placeholder="Enter your full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0A4B4B] focus:border-[#0A4B4B] transition-colors"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0A4B4B] focus:border-[#0A4B4B] transition-colors"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                placeholder="Create a password (min 6 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0A4B4B] focus:border-[#0A4B4B] transition-colors"
                required
                minLength={6}
                disabled={loading}
              />
            </div>

            {/* Approval Info Box */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex">
                <Clock className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-amber-800">Admin Approval Required</h3>
                  <p className="text-sm text-amber-700 mt-1">
                    After registering, an admin will review and approve your account before you can access the platform.
                  </p>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0A4B4B] hover:bg-[#083d3d] disabled:bg-gray-400 text-white py-3 px-4 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Submitting Request...
                </div>
              ) : (
                "Request Access"
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-center text-sm text-gray-600">
              Already have an account?{" "}
              <Link to="/login" className="text-[#0A4B4B] hover:text-[#083d3d] font-semibold transition-colors">
                Sign in here
              </Link>
            </p>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-gray-600 hover:text-[#0A4B4B] transition-colors">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  )
}

export default RegisterPage
